import { useApolloClient } from "@apollo/client";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState, useSetRecoilState } from "recoil";
import * as Yup from "yup";
import { FlexContext } from "../models/flexible/flex-context";
import { FlexEntitySchema } from "../models/flexible/flex-entity-schema";
import { createInitialRequestConfig } from "../models/flexible/initial-request";
import { createTypeInitialValues } from "../models/flexible/initial-values";
import { TypedFlexEntity } from "../models/flexible/typed-flex-entity";
import { createTypeValidationSchema } from "../models/flexible/validation";
import { Case, FlexEntityType } from "../models/generated/graphql";
import { flexibleFormResponseState, pageLoadingState } from "../store/flexible";
import { userPersonalInfoState } from "../store/user";
import { deserializeFlexEntityData } from "../utils/deserialize-flex-entity-data";
import { createSectionController, getInitialFormDataQuery, setFlexControllerReferences } from "../utils/flex-entity-utils";
import { dataModuleString } from "./useDataModule";

type StateType = {
	loading: boolean;
	responseData?: any;
	initialValues?: any;
	validationSchema?: Yup.AnySchema;
	context?: FlexContext;
	error?: Error;
};

export const useDynamicFlexForm = (
	schema: FlexEntitySchema,
	entityData: any,
	isEditable: boolean,
	caseInstance: Case,
	entityType: FlexEntityType
): StateType => {
	const client = useApolloClient();
	const [{ responseData, initialValues, validationSchema, context, error, loading }, setResponse] = useState<StateType>({
		loading: true,
	});
	const { t } = useTranslation();
	const [{ organization }] = useRecoilState(userPersonalInfoState);
	const setGlobalResponse = useSetRecoilState(flexibleFormResponseState);
	const setGlobalLoading = useSetRecoilState(pageLoadingState);

	useEffect(() => {
		const newContext = {
			entityType,
			sectionControllers: schema.sections.map((section) => createSectionController(schema, undefined, section, "")),
			cases: [caseInstance] as Array<Omit<Case, "flexEntities"> & { flexEntities: Array<TypedFlexEntity<any>> }>,
			parameters: {},
			isEditable,
			isNegotiable: true,
			entityData,
			tenantId: organization.externalId,
		} as FlexContext;
		newContext.sectionControllers.forEach((s) => setFlexControllerReferences(s, undefined, newContext));
		const requestConfig = createInitialRequestConfig(newContext);
		setGlobalLoading(true);

		client
			.query({
				query: getInitialFormDataQuery(requestConfig),
				variables: requestConfig.variables,
				context: { module: dataModuleString },
			})
			.then(({ data }) => {
				const deserializedData = deserializeFlexEntityData(data);
				setGlobalResponse((state: any) => ({ ...deserializedData, ...state }));
				setResponse({
					responseData: deserializedData,
					loading: false,
					context: newContext,
					validationSchema: createTypeValidationSchema(newContext, data, t),
					initialValues: createTypeInitialValues(newContext, entityData, data),
				});
				setGlobalLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setResponse({ loading: false, error });
				setGlobalLoading(false);
			});
	}, [schema, entityData]);

	return { responseData, initialValues, validationSchema, context, error, loading };
};
